import { IonAlert, useIonToast } from "@ionic/react";
import { useEffect, useRef, useState } from "react";

import LoginAPI from "src/services/login";
import {
  isWebAuthnSupported,
  isMobileDevice,
  isPlatformAuthenticatorAvailable,
  webauthnDispositivoRegistrar,
} from "src/services/webauthn";

/**
 * Auto-prompt de enrolamiento de la CLAVE DE DISPOSITIVO tras el login.
 *
 * Se ofrece SOLO en CELULAR (nunca en PC), en CADA inicio de la app (una vez por
 * arranque, no durante la navegación), mientras se cumpla:
 *  - el usuario está habilitado (piloto_dispositivo),
 *  - el operario todavía no tiene una clave de dispositivo ACTIVA (dato del
 *    servidor: dispositivo_registrado). Antes se usaba un flag en localStorage,
 *    pero en el navegador móvil no persiste entre aperturas y hacía que se
 *    re-ofreciera y se re-enrolara la clave (credenciales duplicadas).
 *  - el equipo es compatible (autenticador de plataforma disponible).
 *
 * El registro exige un toque del usuario (WebAuthn no permite crear la clave en
 * segundo plano).
 */
interface Props {
  isAuth: boolean;
}

const DispositivoSeguroPrompt: React.FC<Props> = ({ isAuth }) => {
  const [present] = useIonToast();
  const [abierto, setAbierto] = useState(false);
  const [registrando, setRegistrando] = useState(false);
  const chequeadoRef = useRef(false);

  useEffect(() => {
    // Al cerrar sesión permitimos volver a ofrecerlo en el próximo login.
    if (!isAuth) {
      chequeadoRef.current = false;
      return;
    }
    if (chequeadoRef.current) return;
    chequeadoRef.current = true;

    const evaluar = async () => {
      try {
        if (!isWebAuthnSupported()) return;
        if (!isMobileDevice()) return; // la clave vive en el celular: NUNCA en PC

        const resp = await LoginAPI.me();
        const datos = resp?.data?.datos || {};
        if (!datos.piloto_dispositivo) return; // usuario habilitado
        if (datos.dispositivo_registrado) return; // ya tiene clave ACTIVA (verdad del server)

        const capaz = await isPlatformAuthenticatorAvailable();
        if (!capaz) return; // equipo no compatible -> se queda en el tier UUID

        setAbierto(true);
      } catch {
        // best-effort: ante cualquier error no molestamos al usuario
      }
    };

    evaluar();
  }, [isAuth]);

  const registrar = async () => {
    if (registrando) return;
    setRegistrando(true);
    try {
      await webauthnDispositivoRegistrar();
      setAbierto(false);
      present({
        message: "Dispositivo registrado. La próxima vez entrás sin código.",
        duration: 5000,
        color: "success",
      });
    } catch (error: any) {
      const backendMsg = error?.response?.data?.error;
      setAbierto(false);
      present({
        message:
          backendMsg ||
          "No se pudo registrar el dispositivo. Podés intentarlo desde tu perfil.",
        duration: 6000,
        color: "warning",
      });
    } finally {
      setRegistrando(false);
    }
  };

  const ahoraNo = () => {
    // Cierra por ahora; si el operario no enrola, dispositivo_registrado sigue en
    // false y vuelve a aparecer en el próximo inicio de la app. También queda
    // disponible desde el perfil.
    setAbierto(false);
  };

  return (
    <IonAlert
      isOpen={abierto}
      onDidDismiss={() => setAbierto(false)}
      backdropDismiss={false}
      header="Registrar este dispositivo"
      message="Registrá este equipo como tu dispositivo seguro para iniciar sesión sin código la próxima vez."
      buttons={[
        { text: "Ahora no", role: "cancel", handler: ahoraNo },
        { text: "Registrar", handler: () => { registrar(); } },
      ]}
    />
  );
};

export default DispositivoSeguroPrompt;
